import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowUpRight, 
  Award, 
  Briefcase, 
  Target, 
  TrendingUp,
  CheckCircle,
  BarChart3,
  ShoppingBag
} from 'lucide-react';

const AboutPage = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const expertise = [
    {
      icon: <ShoppingBag className="w-5 h-5" />,
      title: 'Crescita Marketplace',
      description: 'eBay, Amazon, ManoMano e espansione multi-canale.'
    },
    {
      icon: <TrendingUp className="w-5 h-5" />,
      title: 'Scaling Ecommerce',
      description: 'Costruzione e ottimizzazione di negozi Shopify per una crescita sostenibile.'
    },
    {
      icon: <Target className="w-5 h-5" />,
      title: 'Digital Marketing',
      description: 'Meta Ads, TikTok Ads e strategie di performance marketing.'
    },
    {
      icon: <BarChart3 className="w-5 h-5" />,
      title: 'Strategia Aziendale',
      description: 'Pianificazione della crescita dei ricavi e ottimizzazione operativa.'
    }
  ];

  const values = [
    {
      title: 'Pensiero Strategico',
      description: 'Ogni decisione è supportata dalla strategia e allineata ai tuoi obiettivi di business.'
    },
    {
      title: 'Focus sull\'Esecuzione',
      description: 'Non ci limitiamo a consigliare — implementiamo e consegniamo risultati.'
    },
    {
      title: 'Data-Driven',
      description: 'Analytics e metriche guidano ogni raccomandazione che facciamo.'
    },
    {
      title: 'Partnership a Lungo Termine',
      description: 'Siamo investiti nel tuo successo, non solo nei risultati a breve termine.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#2DD4BF]/5 rounded-full blur-[100px]" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="text-[#2DD4BF] text-sm font-semibold tracking-wider uppercase mb-4 block">
              Chi Siamo
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1D24] leading-tight tracking-tight mb-6">
              Costruiamo il Successo Digitale Attraverso la Consulenza Strategica
            </h1>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              Carone Consulting è un'agenzia di consulenza digitale e crescita ecommerce dedicata ad aiutare le aziende a scalare attraverso strategie collaudate e un'esecuzione pratica.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Founder Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_e560243f-9eed-4536-b73b-967f4dba2a94/artifacts/vbx6g0ni_IMG-20240509-WA0092.jpg"
                  alt="Paolo Carone - Fondatore"
                  className="w-full h-auto rounded-lg shadow-2xl object-cover object-top grayscale hover:grayscale-0 transition-all duration-500"
                  style={{ maxHeight: '600px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent rounded-lg" />
              </div>
              {/* Decorative Element */}
              <div className="absolute -z-10 top-6 -left-6 w-full h-full border-2 border-[#2DD4BF]/30 rounded-lg" />
              
              {/* Experience Badge */}
              <div className="absolute bottom-6 right-6 bg-white p-6 rounded-lg shadow-xl border border-[#E5E7EB]">
                <div className="flex items-center gap-3">
                  <Award className="w-8 h-8 text-[#2DD4BF]" />
                  <div>
                    <div className="text-[#1A1D24] font-semibold">10+ Anni</div>
                    <div className="text-[#6B7280] text-sm">Esperienza nel Settore</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Founder Info */}
            <motion.div {...fadeUp}>
              <span className="text-[#2DD4BF] text-sm font-semibold tracking-wider uppercase mb-4 block">
                Il Fondatore
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1D24] tracking-tight mb-6">
                Paolo Carone
              </h2>
              <p className="text-[#2DD4BF] font-medium mb-6">Fondatore & Consulente Digitale</p>
              <div className="space-y-4 text-[#4B5563] leading-relaxed mb-8">
                <p>
                  Imprenditore e consulente ecommerce con vasta esperienza nell'aiutare le aziende a raggiungere una crescita misurabile nello spazio digitale.
                </p>
                <p>
                  Con un background che spazia dall'ottimizzazione dei marketplace, allo scaling ecommerce e alla strategia di digital marketing, Paolo porta una combinazione unica di visione strategica e capacità di esecuzione in ogni progetto.
                </p>
                <p>
                  Il suo approccio si concentra sulla costruzione di sistemi scalabili, sfruttando insight data-driven e consegnando risultati tangibili che impattano sul fatturato.
                </p>
              </div>

              {/* Expertise List */}
              <div className="grid sm:grid-cols-2 gap-4">
                {expertise.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-start gap-3 p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg"
                  >
                    <div className="text-[#2DD4BF] mt-0.5">{item.icon}</div>
                    <div>
                      <h4 className="text-[#1A1D24] font-semibold text-sm mb-1">{item.title}</h4>
                      <p className="text-[#6B7280] text-xs">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-[#F4F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="text-[#2DD4BF] text-sm font-semibold tracking-wider uppercase mb-4 block">
              Il Nostro Approccio
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1D24] tracking-tight">
              Cosa Guida il Nostro Lavoro
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-white border border-[#E5E7EB] p-8 rounded-lg h-full hover:shadow-lg transition-shadow">
                  <div className="text-4xl font-bold text-[#2DD4BF]/20 mb-4">
                    0{index + 1}
                  </div>
                  <h3 className="text-xl font-semibold text-[#1A1D24] mb-3">
                    {value.title}
                  </h3>
                  <p className="text-[#6B7280] text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Authority Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <span className="text-[#2DD4BF] text-sm font-semibold tracking-wider uppercase mb-4 block">
                Perché Lavorare con Noi
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1D24] tracking-tight mb-6">
                Autorità Costruita sui Risultati
              </h2>
              <p className="text-[#4B5563] leading-relaxed mb-8">
                Abbiamo aiutato aziende di diversi settori a raggiungere i loro obiettivi di crescita. Il nostro track record parla da solo — risultati misurabili, sistemi scalabili e crescita sostenibile.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  'Strategie comprovate di crescita marketplace',
                  'Decisioni basate sui dati',
                  'Supporto pratico nell\'implementazione',
                  'Approccio di partnership a lungo termine',
                  'Focus su ROI e risultati misurabili'
                ].map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-[#2DD4BF] flex-shrink-0" />
                    <span className="text-[#4B5563]">{item}</span>
                  </motion.div>
                ))}
              </div>

              <Link
                to="/contact"
                data-testid="about-cta-button"
                className="inline-flex items-center gap-2 bg-[#2DD4BF] text-white px-8 py-4 font-semibold rounded-sm hover:bg-[#14B8A6] transition-all hover:scale-105 shadow-lg"
              >
                Inizia una Conversazione
                <ArrowUpRight size={20} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-6"
            >
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-8 rounded-lg text-center">
                <Briefcase className="w-8 h-8 text-[#2DD4BF] mx-auto mb-4" />
                <div className="text-3xl font-bold text-[#1A1D24] mb-2">50+</div>
                <p className="text-[#6B7280] text-sm">Progetti Consegnati</p>
              </div>
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-8 rounded-lg text-center">
                <TrendingUp className="w-8 h-8 text-[#2DD4BF] mx-auto mb-4" />
                <div className="text-3xl font-bold text-[#1A1D24] mb-2">240%</div>
                <p className="text-[#6B7280] text-sm">Crescita Media</p>
              </div>
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-8 rounded-lg text-center">
                <Award className="w-8 h-8 text-[#2DD4BF] mx-auto mb-4" />
                <div className="text-3xl font-bold text-[#1A1D24] mb-2">10+</div>
                <p className="text-[#6B7280] text-sm">Anni di Esperienza</p>
              </div>
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-8 rounded-lg text-center">
                <Target className="w-8 h-8 text-[#2DD4BF] mx-auto mb-4" />
                <div className="text-3xl font-bold text-[#1A1D24] mb-2">€5M+</div>
                <p className="text-[#6B7280] text-sm">Ricavi Generati</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative overflow-hidden bg-gradient-to-br from-[#F4F5F7] to-[#E8E9EB]">
        <div className="absolute inset-0 bg-gradient-to-r from-[#2DD4BF]/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div {...fadeUp} className="text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1D24] tracking-tight mb-6">
              Costruiamo Qualcosa di Grande Insieme
            </h2>
            <p className="text-[#6B7280] text-lg max-w-xl mx-auto mb-10">
              Pronto a portare il tuo business digitale al livello successivo? Parliamo dei tuoi obiettivi.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-final"
              className="inline-flex items-center gap-2 bg-[#2DD4BF] text-white px-8 py-4 font-semibold rounded-sm hover:bg-[#14B8A6] transition-all hover:scale-105 shadow-lg"
            >
              Richiedi una Consulenza
              <ArrowUpRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
